import React, { useContext, useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { ThemeContext } from "../../../../..";
import { getData } from "../../../../../Services/Services";
import { getBlockInfoFloodURL } from "../../../../../Services/EndPoints";
import BlockPopUp from "../../BlockPopUp";
import DistrictBlockDetails from "../../DistrictBlockDetails";

const FloodBlocksTreemap = ({ title, dataval, startDateStr, endDateStr }) => {
  const { theme } = useContext(ThemeContext);
  const echartsDom = useRef(null);
  const [distDetails, setDistDetails] = useState([]);
  const [activeTab, setActiveTab] = useState("Affected");
  const [popUp, setPopUp] = useState(false);

  const dataValue = dataval && dataval[0];
  const keys = Object?.keys(dataValue || {});

  const getMatchingKey = (keys, patterns) => {
    for (let key of keys) { for (let pattern of patterns) { if (key.includes(pattern)) return key; } }
    return null;
  };

  const totalKey = getMatchingKey(keys, ["total_blocks", "total"]);
  const affectedKey = getMatchingKey(keys, ["affected_blocks", "affected"]);
  const partialKey = getMatchingKey(keys, ["partially_affected"]);

  const total = dataValue?.[totalKey] || 0;
  const affected = dataValue?.[affectedKey] || 0;
  const partiallyAffected = dataValue?.[partialKey] || 0;
  const nonAffected = total > (affected + partiallyAffected) ? total - (affected + partiallyAffected) : 0;

  const handleToggle = () => setPopUp(!popUp);

  const columns = React.useMemo(() => [
    { Header: "Block Name", accessor: "block_name", disableSortBy: true, centerAlign: true }
  ], []);

  const fetchData = async (apiKey) => {
    try {
      const response = await getData(
        getBlockInfoFloodURL.replace("TODATE", endDateStr).replace("DISTID", 0).replace("APIKEY", apiKey).replace("FROMDATE", startDateStr)
      );
      setDistDetails(response);
    } catch (error) { console.error("Error fetching block data:", error); }
  };

  useEffect(() => {
    if (popUp) { fetchData("get_affected_blocks"); }
  }, [activeTab, popUp]);

  useEffect(() => {
    const myChart = echarts.init(echartsDom.current);
    const isLightTheme = theme === "light";

    const affectedPct = total > 0 ? ((affected / total) * 100).toFixed(1) : 0;
    const totalPct = total > 0 ? (((affected + partiallyAffected) / total) * 100).toFixed(1) : 0;

    const option = {
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        formatter: `<div style="padding:6px 10px">
          <div style="font-weight:700;color:#1C4E72;margin-bottom:4px">${title}</div>
          <div>{b}: <b>{c}</b> ({d}%)</div>
          <div style="margin-top:4px;color:#7BA5BE">Total: <b>${total}</b></div>
        </div>`,
        backgroundColor: '#fff',
        borderColor: '#eee',
        borderWidth: 1,
        extraCssText: "border-radius:10px;box-shadow:0 4px 16px rgba(0,0,0,.08);"
      },
      legend: {
        bottom: '0%',
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          fontSize: 10,
          fontWeight: '600',
          color: '#3A5F78'
        }
      },
      series: [
        {
          name: title,
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
              color: '#1C4E72',
              formatter: '{c}'
            }
          },
          labelLine: {
            show: false
          },
          data: [
            { value: affected, name: 'Affected', itemStyle: { color: '#0F2D42' } },
            { value: partiallyAffected, name: 'Partially Affected', itemStyle: { color: '#7BA5BE' } },
            { value: nonAffected, name: 'Non Affected', itemStyle: { color: '#D9E6F2' } }
          ]
        }
      ]
    };

    myChart.setOption(option);
    myChart.on('click', () => { setPopUp(true); });

    const resize = () => myChart.resize();
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); myChart.off("click"); myChart.dispose(); };
  }, [dataval, theme]);

  return (
    <div className="card h-100 shadow-sm border-0" style={{ borderRadius: "14px", overflow: "hidden", background: "var(--av-surface)" }}>
      <div className="card-body d-flex flex-column h-100 p-3">
        <div className="card-title fw-bold" style={{ color: "var(--av-primary)", fontSize: "0.80rem", textTransform: "uppercase", letterSpacing: "1px" }}>
          {title}
        </div>
        <div ref={echartsDom} style={{ flex: 1, width: "100%", minHeight: "220px", cursor: "pointer" }} />
      </div>
      {popUp && (
        <BlockPopUp title={`${title} Details`} show={popUp} handleToggle={handleToggle}
          component={<DistrictBlockDetails data={distDetails} column={columns} activeTab={activeTab} setActiveTab={setActiveTab} />}
        />
      )}
    </div>
  );
};

export default FloodBlocksTreemap;
